import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";

import { ReadonlyAbortNotifier } from "./userPerceivedLoadTiming/AbortNotifier";
import { LoadTimingStatus } from "./userPerceivedLoadTiming/types";

export interface IQuietMainThreadResult {
  startOfQuietWindow?: number;
  status: "success" | "page-hidden" | "aborted";
  startOfTracking?: number;
  frameDurations?: number[];
}

export const detectQuietMainThread = (
  maxQuietWindowWaitTimeMs: number,
  frameLatencyThresholdMs: number,
  abortNotifier?: ReadonlyAbortNotifier<LoadTimingStatus>,
  ignoreAbortReasons?: LoadTimingStatus[]
): Promise<IQuietMainThreadResult> =>
  new Promise((resolve) => {
    const {
      document,
      requestAnimationFrame,
      cancelAnimationFrame,
      performance,
    } = getWindowOrThrow();

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        cleanupAndResolve({ status: "page-hidden" });
      }
    };

    const cleanupAndResolve = (result: IQuietMainThreadResult) => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (rafHandle != null) {
        cancelAnimationFrame(rafHandle);
      }
      resolve(result);
    };

    let startOfQuietWindow = performance.now();
    const startOfTracking = startOfQuietWindow;
    let rafHandle: number | null = null;
    let lastFrame = startOfQuietWindow;
    const frameDurations: number[] = [];

    const onNextFrame = () => {
      const thisFrame = performance.now();
      const actualInterval = thisFrame - lastFrame;
      frameDurations.push(actualInterval);

      if (actualInterval >= frameLatencyThresholdMs) {
        // Long task detected, reset the window start time
        startOfQuietWindow = thisFrame;
      }

      if (thisFrame - startOfQuietWindow < maxQuietWindowWaitTimeMs) {
        lastFrame = thisFrame;
        rafHandle = requestAnimationFrame(onNextFrame);
      } else {
        cleanupAndResolve({
          startOfQuietWindow,
          status: "success",
          startOfTracking,
          frameDurations,
        });
      }
    };

    if (abortNotifier?.aborted) {
      if (
        !ignoreAbortReasons?.includes(abortNotifier?.reason as LoadTimingStatus)
      ) {
        resolve({
          status: "aborted",
        });
      }
    } else if (document.visibilityState !== "visible") {
      resolve({
        status: "page-hidden",
      });
    } else {
      abortNotifier?.addAbortListener((reason) => {
        if (!reason || !ignoreAbortReasons?.includes(reason)) {
          cleanupAndResolve({ status: "aborted" });
        }
      });
      document.addEventListener("visibilitychange", onVisibilityChange);
      rafHandle = requestAnimationFrame(onNextFrame);
    }
  });
